import React, { useMemo } from 'react';
import { Control, useController } from 'react-hook-form';

import { TravellersItem } from '@ott/travellers';
import TravellersChildren from '@ott/travellers-children';
import PlusMinus from '@ott/plus-minus';
import { l10n } from '@ott/l10n';

import {
  MIN_ADULTS,
  MAX_ADULTS,
  MAX_CHILDREN,
  MAX_CHILDREN_AGE,
  MAX_INFANT_AGE,
} from '../../../../constants';

import { FormState, Room } from '../../../../types';

import getGuestsCount from '../../../../utilities/getGuestsCount';

import styles from './RoomPicker.scss';

const childrenClassNames = {
  quantity: styles.guestsCount,
};

interface Props {
  index: number;
  onDelete: (index: number) => void;
  hasHeader: boolean;
  control: Control<FormState>;
  maxGuestsCount: number;
  rooms: Room[];
}

const RoomPicker: React.FC<Props> = (props) => {
  const { index, onDelete, hasHeader, control, rooms, maxGuestsCount } = props;

  const {
    field: { value: adults, onChange: onAdultsChange },
  } = useController({
    name: `rooms.${index}.adults`,
    control,
  });

  const {
    field: { value: childrenAges, onChange: onChildrenAgesChange },
  } = useController({
    name: `rooms.${index}.childrenAges`,
    control,
  });

  const currentGuestsCount = getGuestsCount(rooms, false);

  let maxAdults = MAX_ADULTS;
  let maxChildrenAge = MAX_CHILDREN_AGE;
  const maxChildren = MAX_CHILDREN;

  if (maxGuestsCount && currentGuestsCount >= maxGuestsCount) {
    maxAdults = adults;
    maxChildrenAge = MAX_INFANT_AGE;
  }

  const childrenAgeRange = useMemo(() => ({ start: 0, end: maxChildrenAge }), [maxChildrenAge]);

  const adultsNode = (
    <PlusMinus
      min={MIN_ADULTS}
      max={maxAdults}
      value={adults}
      label={`${adults} ${l10n('adults', { value: adults })}`}
      onChange={onAdultsChange}
      childrenClassNames={childrenClassNames}
    />
  );
  const childrenNode = (
    <TravellersChildren
      maxChildren={maxChildren}
      childrenAgeRange={childrenAgeRange}
      childrenAges={childrenAges}
      onChildrenAgesChange={onChildrenAgesChange}
    />
  );

  return (
    <TravellersItem
      withHeader={hasHeader}
      index={index}
      dataLocator="delete-room"
      onDeleteGroup={onDelete}
      adultsNode={adultsNode}
      childrenNode={childrenNode}
    />
  );
};

export default RoomPicker;
